import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import {
  Building2,
  Globe,
  Mail,
  ShieldCheck,
  Calendar,
  Plus,
  X,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  UserPlus,
} from 'lucide-react';
import { formatDate, cn } from '@/lib/utils';
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';

export const Route = createFileRoute('/clients/clients')({
  component: ClientsComponent,
})

interface Client {
  id: string;
  name: string;
  email: string;
  slug: string;
  planTier: 'standard' | 'enterprise';
  isActive: boolean;
  billingAnchorDay: number;
  createdAt: string;
}

interface JoinRequest {
  id: string;
  userId: string;
  organizationSlug: string;
  clientId: string | null;
  status: 'pending' | 'approved' | 'denied';
  assignedRole: string | null;
  denyReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  userEmail: string;
  userFirstName: string | null;
  userLastName: string | null;
  clientName: string | null;
  clientSlug: string | null;
}

type Tab = 'organizations' | 'join-requests';

function ClientsComponent() {
  const [activeTab, setActiveTab] = useState<Tab>('organizations');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: clients, isLoading, error } = useQuery<Client[]>({
    queryKey: ['admin', 'clients'],
    queryFn: async () => {
      const response = await api.clients.listClients();
      const result = (response && typeof response === 'object' && 'data' in response)
        ? response.data
        : response;
      return (Array.isArray(result) ? result : []) as Client[];
    },
  });

  const { data: joinRequests } = useQuery<JoinRequest[]>({
    queryKey: ['admin', 'join-requests'],
    queryFn: async () => {
      const result = await api.onboarding.listJoinRequests();
      return (Array.isArray(result) ? result : []) as JoinRequest[];
    },
  });

  const pendingCount = joinRequests?.filter(r => r.status === 'pending').length || 0;

  if (isLoading) return <div className="p-8 text-slate-400 font-mono text-xs uppercase tracking-widest animate-pulse">Loading Organizations...</div>;
  if (error) return <div className="p-8 text-red-400 font-mono text-xs uppercase tracking-widest">Error loading clients.</div>;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-200 p-8 font-sans">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase">Client Organizations</h1>
          <p className="text-slate-500 text-sm mt-1">Manage organizations, users, and onboarding requests.</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} />
          Add Client
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-800">
        <button
          onClick={() => setActiveTab('organizations')}
          className={cn(
            'px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
            activeTab === 'organizations'
              ? 'border-blue-500 text-blue-400'
              : 'border-transparent text-slate-500 hover:text-slate-300'
          )}
        >
          <span className="flex items-center gap-2">
            <Building2 size={14} />
            Organizations
            <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded font-bold">
              {clients?.length || 0}
            </span>
          </span>
        </button>
        <button
          onClick={() => setActiveTab('join-requests')}
          className={cn(
            'px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px',
            activeTab === 'join-requests'
              ? 'border-blue-500 text-blue-400'
              : 'border-transparent text-slate-500 hover:text-slate-300'
          )}
        >
          <span className="flex items-center gap-2">
            <UserPlus size={14} />
            Join Requests
            {pendingCount > 0 && (
              <span className="text-[10px] bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center">
                {pendingCount}
              </span>
            )}
          </span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'organizations' && <ClientsTable clients={clients || []} />}
      {activeTab === 'join-requests' && <JoinRequestsPanel joinRequests={joinRequests || []} clients={clients || []} />}

      {/* Create Client Modal */}
      {showCreateModal && <CreateClientModal onClose={() => setShowCreateModal(false)} />}
    </div>
  );
}

// =============================================================================
// Clients Table
// =============================================================================

function ClientsTable({ clients }: { clients: Client[] }) {
  const navigate = useNavigate();

  return (
    <div className="bg-[#151921] rounded-xl border border-slate-800 overflow-hidden">
      <div className="p-5 border-b border-slate-800 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-400 tracking-wider uppercase">Active Directories</h3>
        <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-1 rounded font-bold">
          {clients.length} TOTAL
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border-collapse">
          <thead className="bg-[#1A1F29] text-slate-500 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="px-6 py-4 font-medium">Organization</th>
              <th className="px-6 py-4 font-medium">Slug / Domain</th>
              <th className="px-6 py-4 font-medium">Plan</th>
              <th className="px-6 py-4 font-medium">Status</th>
              <th className="px-6 py-4 font-medium">Billing Anchor</th>
              <th className="px-6 py-4 font-medium">Onboarded</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {clients.map((client) => (
              <tr
                key={client.id}
                onClick={() => navigate({
                  to: '/clients/$clientId',
                  params: { clientId: client.id }
                })}
                className="hover:bg-slate-800/30 transition-colors cursor-pointer group"
              >
                <td className="px-6 py-4 align-middle">
                  <div className="flex flex-col justify-center">
                    <span className="font-medium text-slate-100 group-hover:text-cyan-400 transition-colors flex items-center gap-2">
                      <Building2 size={14} className="text-slate-500" />
                      {client.name}
                    </span>
                    <span className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <Mail size={10} />
                      {client.email}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 align-middle font-mono text-[12px] text-slate-400">
                  <div className="flex items-center gap-2 h-full">
                    <Globe size={12} className="text-slate-600" />
                    {client.slug}
                  </div>
                </td>
                <td className="px-6 py-4 align-middle">
                  <div className="flex items-center h-full">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${
                      client.planTier === 'enterprise' 
                        ? 'border-purple-500/50 text-purple-400 bg-purple-500/10' 
                        : 'border-blue-500/50 text-blue-400 bg-blue-500/10'
                    }`}>
                      {client.planTier}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 align-middle">
                  <div className="flex items-center gap-1.5 h-full">
                    <div className={`h-1.5 w-1.5 rounded-full ${client.isActive ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-slate-600'}`} />
                    <span className={client.isActive ? 'text-green-400 text-[12px] font-bold' : 'text-slate-500 text-[12px] font-bold'}>
                      {client.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 align-middle">
                  <div className="flex items-center gap-2 text-slate-400 font-mono text-xs h-full">
                    <ShieldCheck size={14} className="text-slate-600" />
                    <span>DAY {client.billingAnchorDay}</span>
                  </div>
                </td>
                <td className="px-6 py-4 align-middle text-slate-500 text-[12px] font-mono">
                  <div className="flex items-center gap-2 h-full">
                    <Calendar size={12} />
                    {formatDate(new Date(client.createdAt))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Create Client Modal
// =============================================================================

function CreateClientModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    email: '',
    slug: '',
    planTier: 'standard' as 'standard' | 'professional' | 'enterprise',
    billingAnchorDay: 1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: (data: typeof form) => api.clients.create(data),
    onSuccess: () => {
      toast({ title: 'Client Created', description: `${form.name} has been added.` });
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] });
      onClose();
    },
    onError: (error: any) => {
      // The API returns a message like "A client with the same name, email already exists."
      const message = error?.message || 'Failed to create client';
      // Try to highlight conflicting fields from the error message
      const fieldErrors: Record<string, string> = {};
      if (message.includes('name')) fieldErrors.name = 'Already in use';
      if (message.includes('email')) fieldErrors.email = 'Already in use';
      if (message.includes('slug')) fieldErrors.slug = 'Already in use';
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
      }
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setForm(prev => ({ ...prev, name, slug }));
    setErrors(prev => { const { name: _, slug: __, ...rest } = prev; return rest; });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email address';
    if (!form.slug.trim()) errs.slug = 'Slug is required';
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) errs.slug = 'Lowercase letters, numbers, and hyphens only';
    if (form.billingAnchorDay < 1 || form.billingAnchorDay > 28) errs.billingAnchorDay = 'Must be between 1 and 28';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    createMutation.mutate(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[#151921] border border-slate-700 rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Building2 size={16} className="text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-100">Add New Client</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-slate-300 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-xs text-slate-400 uppercase tracking-widest mb-2 font-medium">
              Organization Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Acme Corporation"
              className={cn(
                'w-full px-4 py-2.5 bg-[#0B0E14] border rounded-lg text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm',
                errors.name ? 'border-red-500/50' : 'border-slate-700'
              )}
            />
            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs text-slate-400 uppercase tracking-widest mb-2 font-medium">
              Contact Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => { setForm(prev => ({ ...prev, email: e.target.value })); setErrors(prev => { const { email: _, ...rest } = prev; return rest; }); }}
              placeholder="admin@acme.com"
              className={cn(
                'w-full px-4 py-2.5 bg-[#0B0E14] border rounded-lg text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm',
                errors.email ? 'border-red-500/50' : 'border-slate-700'
              )}
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Slug */}
          <div>
            <label className="block text-xs text-slate-400 uppercase tracking-widest mb-2 font-medium">
              Slug <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => { setForm(prev => ({ ...prev, slug: e.target.value.toLowerCase() })); setErrors(prev => { const { slug: _, ...rest } = prev; return rest; }); }}
              placeholder="acme-corp"
              className={cn(
                'w-full px-4 py-2.5 bg-[#0B0E14] border rounded-lg text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm font-mono',
                errors.slug ? 'border-red-500/50' : 'border-slate-700'
              )}
            />
            <p className="text-slate-600 text-xs mt-1">Users will enter this to request to join the organization</p>
            {errors.slug && <p className="text-red-400 text-xs mt-1">{errors.slug}</p>}
          </div>

          {/* Plan Tier + Billing Anchor */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 uppercase tracking-widest mb-2 font-medium">
                Plan Tier
              </label>
              <select
                value={form.planTier}
                onChange={(e) => setForm(prev => ({ ...prev, planTier: e.target.value as typeof form.planTier }))}
                className="w-full px-4 py-2.5 bg-[#0B0E14] border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm"
              >
                <option value="standard">Standard</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 uppercase tracking-widest mb-2 font-medium">
                Billing Anchor Day
              </label>
              <input
                type="number"
                min={1}
                max={28}
                value={form.billingAnchorDay}
                onChange={(e) => setForm(prev => ({ ...prev, billingAnchorDay: parseInt(e.target.value) || 1 }))}
                className={cn(
                  'w-full px-4 py-2.5 bg-[#0B0E14] border rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm',
                  errors.billingAnchorDay ? 'border-red-500/50' : 'border-slate-700'
                )}
              />
              {errors.billingAnchorDay && <p className="text-red-400 text-xs mt-1">{errors.billingAnchorDay}</p>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Plus size={14} />
              )}
              Create Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Join Requests Panel
// =============================================================================

function JoinRequestsPanel({ joinRequests, clients }: { joinRequests: JoinRequest[]; clients: Client[] }) {
  const pending = joinRequests.filter(r => r.status === 'pending');
  const resolved = joinRequests.filter(r => r.status !== 'pending');

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      <div className="bg-[#151921] rounded-xl border border-slate-800 overflow-hidden">
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-400 tracking-wider uppercase">Pending Requests</h3>
          {pending.length > 0 && (
            <span className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-1 rounded font-bold">
              {pending.length} AWAITING REVIEW
            </span>
          )}
        </div>

        {pending.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <CheckCircle2 size={24} className="text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No pending requests</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {pending.map((req) => (
              <JoinRequestRow key={req.id} request={req} clients={clients} />
            ))}
          </div>
        )}
      </div>

      {/* Resolved Requests */}
      {resolved.length > 0 && (
        <div className="bg-[#151921] rounded-xl border border-slate-800 overflow-hidden">
          <div className="p-5 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-slate-400 tracking-wider uppercase">Resolved Requests</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#1A1F29] text-slate-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="px-6 py-3 font-medium">User</th>
                  <th className="px-6 py-3 font-medium">Requested Org</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Role</th>
                  <th className="px-6 py-3 font-medium">Reviewed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {resolved.map((req) => (
                  <tr key={req.id} className="text-slate-400">
                    <td className="px-6 py-3">
                      <div>
                        <span className="text-slate-200 text-sm">
                          {req.userFirstName} {req.userLastName}
                        </span>
                        <span className="block text-[11px] text-slate-500">{req.userEmail}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 font-mono text-[12px]">{req.organizationSlug}</td>
                    <td className="px-6 py-3">
                      <span className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border uppercase',
                        req.status === 'approved'
                          ? 'border-green-500/50 text-green-400 bg-green-500/10'
                          : 'border-red-500/50 text-red-400 bg-red-500/10'
                      )}>
                        {req.status === 'approved' ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                        {req.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-[12px] capitalize">{req.assignedRole || '-'}</td>
                    <td className="px-6 py-3 text-[12px] font-mono">
                      {req.reviewedAt ? formatDate(new Date(req.reviewedAt)) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Single Join Request Row (Pending) with Approve/Deny actions
// =============================================================================

function JoinRequestRow({ request, clients }: { request: JoinRequest; clients: Client[] }) {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [selectedClientId, setSelectedClientId] = useState(request.clientId || '');
  const [denyReason, setDenyReason] = useState('');
  const [showDeny, setShowDeny] = useState(false);

  const reviewMutation = useMutation({
    mutationFn: (data: { action: 'approve' | 'deny'; clientId?: string; role?: string; denyReason?: string }) =>
      api.onboarding.reviewJoinRequest(request.id, data),
    onSuccess: (_, variables) => {
      toast({
        title: variables.action === 'approve' ? 'Request Approved' : 'Request Denied',
        description: variables.action === 'approve'
          ? `${request.userFirstName || request.userEmail} has been added to the organization.`
          : `Request from ${request.userFirstName || request.userEmail} has been denied.`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'join-requests'] });
    },
  });

  const handleApprove = () => {
    if (!selectedClientId) {
      toast({ title: 'Select an organization', description: 'Choose which organization to assign the user to.', variant: 'destructive' });
      return;
    }
    reviewMutation.mutate({ action: 'approve', clientId: selectedClientId, role: selectedRole });
  };

  const handleDeny = () => {
    reviewMutation.mutate({ action: 'deny', denyReason: denyReason || undefined });
  };

  return (
    <div className="px-6 py-5">
      {/* User Info */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 font-semibold text-sm">
            {request.userFirstName?.[0] || request.userEmail?.[0] || 'U'}
          </div>
          <div>
            <p className="text-slate-100 font-medium text-sm">
              {request.userFirstName ? `${request.userFirstName} ${request.userLastName || ''}`.trim() : 'Unknown User'}
            </p>
            <p className="text-slate-500 text-[11px]">{request.userEmail}</p>
          </div>
        </div>
        <div className="text-right">
          <span className="flex items-center gap-1.5 text-amber-400 text-[10px] font-bold uppercase">
            <Clock size={10} />
            Pending
          </span>
          <p className="text-slate-600 text-[11px] mt-0.5">{formatDate(new Date(request.createdAt))}</p>
        </div>
      </div>

      {/* Requested org */}
      <div className="mb-4 px-3 py-2 bg-[#0B0E14] rounded-lg border border-slate-800">
        <span className="text-[10px] text-slate-500 uppercase tracking-widest">Requested Organization</span>
        <p className="text-slate-300 font-mono text-sm mt-0.5">{request.organizationSlug}</p>
        {request.clientName && (
          <p className="text-[11px] text-green-400 mt-0.5">Matched: {request.clientName}</p>
        )}
      </div>

      {/* Approve Controls */}
      {!showDeny && (
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-[10px] text-slate-500 uppercase tracking-widest mb-1.5 font-medium">Assign to Organization</label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="w-full px-3 py-2 bg-[#0B0E14] border border-slate-700 rounded-lg text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="">Select organization...</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.slug})
                </option>
              ))}
            </select>
          </div>
          <div className="w-36">
            <label className="block text-[10px] text-slate-500 uppercase tracking-widest mb-1.5 font-medium">Role</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2 bg-[#0B0E14] border border-slate-700 rounded-lg text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="viewer">Viewer</option>
              <option value="credit_analyst">Analyst</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            onClick={handleApprove}
            disabled={reviewMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {reviewMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Approve
          </button>
          <button
            onClick={() => setShowDeny(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm font-medium transition-colors"
          >
            <XCircle size={14} />
            Deny
          </button>
        </div>
      )}

      {/* Deny Form */}
      {showDeny && (
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] text-slate-500 uppercase tracking-widest mb-1.5 font-medium">Reason for Denial (optional)</label>
            <input
              type="text"
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              placeholder="e.g. Unrecognized organization"
              className="w-full px-3 py-2 bg-[#0B0E14] border border-slate-700 rounded-lg text-slate-200 text-sm placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500/50"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDeny}
              disabled={reviewMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {reviewMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
              Confirm Deny
            </button>
            <button
              onClick={() => setShowDeny(false)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
